import type { PaymentAutoSource } from '@/config/shared/auto-source';
import type { PaymentMethod as EventPaymentMethod } from '@/events/conversion';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import { BaseAutoTracker } from '../../auto/BaseAutoTracker';
import { buildAutoSourceOrigin } from '../../auto/build-auto-source-origin';
import type { ConversionEventOrigin } from '../../contracts';
import type { TrackerFactory } from '../../tracks/TrackerFactory';
import { scanPaymentInputStarted } from './card-field-detection';

const FIELD_EVENTS = ['input', 'change', 'focusin'] as const;

export type CardDetectorOptions = {
  readonly source?: PaymentAutoSource;
  readonly origin?: ConversionEventOrigin;
};

export class CardDetector extends BaseAutoTracker {
  private readonly factory: TrackerFactory;

  private readonly paymentMethod: EventPaymentMethod;

  private readonly fireOnce: boolean;

  private readonly formSelector: string | undefined;

  private readonly origin: ConversionEventOrigin;

  private checkTimer: ReturnType<typeof setTimeout> | null = null;

  private tracked = false;

  constructor(
    context: PixelRuntimeContext,
    factory: TrackerFactory,
    options?: CardDetectorOptions
  ) {
    super(context);
    this.factory = factory;
    const source = options?.source;
    this.paymentMethod = normalizePaymentMethod(source?.paymentMethod);
    this.fireOnce = source?.fireOnce !== false;
    this.formSelector = source?.formSelector;
    this.origin =
      options?.origin ??
      (source
        ? buildAutoSourceOrigin('payment', 'ADD_PAYMENT_INFO', source.name)
        : 'auto:card');
  }

  get name(): string {
    const sourceName = this.origin.includes(':')
      ? this.origin.split(':').pop()
      : 'card';
    return `CardDetector(${sourceName})`;
  }

  protected onInit(): void {
    this.tracked = false;
    if (!this.isDocumentAvailable()) return;

    for (const eventName of FIELD_EVENTS) {
      this.addDocumentListener(eventName, () => this.scheduleCheck(), true);
    }

    this.observeDom(() => this.scheduleCheck());
    this.scheduleCheck();
  }

  protected onDestroy(): void {
    this.checkTimer = this.clearManagedTimeout(this.checkTimer);
    this.tracked = false;
  }

  private scheduleCheck(): void {
    if (this.fireOnce && this.tracked) return;
    this.checkTimer = this.clearManagedTimeout(this.checkTimer);
    this.checkTimer = setTimeout(() => {
      this.checkTimer = null;
      this.evaluate();
    }, 300);
  }

  private evaluate(): void {
    if ((this.fireOnce && this.tracked) || !this.isDocumentAvailable()) return;

    const root = this.resolveFormRoot();
    if (this.formSelector && !root) return;
    if (!scanPaymentInputStarted(root)) return;

    this.tracked = true;
    this.factory.trackAuto(
      'ADD_PAYMENT_INFO',
      { paymentMethod: this.paymentMethod },
      { origin: this.origin }
    );
  }

  /**
   * Retorna o container do formSelector, ou document se não houver restrição.
   * null = formSelector configurado mas não encontrado.
   */
  private resolveFormRoot(): ParentNode | null {
    if (!this.formSelector || typeof document === 'undefined') {
      return typeof document !== 'undefined' ? document : null;
    }
    try {
      return document.querySelector(this.formSelector);
    } catch {
      return null;
    }
  }
}

function normalizePaymentMethod(value: string | undefined): EventPaymentMethod {
  switch (value) {
    case 'CREDIT_CARD':
    case 'PIX':
    case 'BOLETO':
    case 'PAYPAL':
    case 'OTHER':
      return value;
    case 'BANK_SLIP':
      return 'BOLETO';
    default:
      return 'CREDIT_CARD';
  }
}
